import type { UserRole } from '@adweb/shared';

/**
 * Contenu de l'aide intégrée (mode d'emploi), filtré selon le rôle connecté.
 *
 * Public de chaque section :
 *  - `all`         : tout utilisateur connecté ;
 *  - `clientAdmin` : administrateurs clients (et Adweb-Systèmes) ;
 *  - `platform`    : rôles Adweb-Systèmes uniquement.
 *
 * Le filtrage est un confort de lecture : ce texte fait partie du code envoyé au
 * navigateur, il ne doit donc contenir ni secret, ni URL technique, ni détail
 * d'infrastructure (ceux-ci restent dans docs/).
 *
 * À mettre à jour dans la même PR que toute évolution visible de l'interface,
 * captures comprises : `pnpm help:screenshots` les régénère (données fictives).
 */
export type HelpAudience = 'all' | 'clientAdmin' | 'platform';

export type HelpBlock =
  | { type: 'p'; text: string }
  | { type: 'steps'; items: string[] }
  | { type: 'list'; items: Array<string | { term: string; text: string }> }
  | { type: 'tip'; text: string }
  /** Capture d'écran (apps/web/public/aide/<shot>.png, générée par `pnpm help:screenshots`). */
  | { type: 'image'; shot: string; alt: string; caption: string; width: number; height: number };

export interface HelpSection {
  id: string;
  title: string;
  audience: HelpAudience;
  blocks: HelpBlock[];
}

export interface HelpGroup {
  title: string;
  sections: HelpSection[];
}

const PLATFORM: ReadonlySet<UserRole> = new Set(['SUPER_ADMIN', 'ADWEB_OPERATOR']);

export function canSee(audience: HelpAudience, role: UserRole): boolean {
  if (audience === 'all') return true;
  if (audience === 'clientAdmin') return role === 'CLIENT_ADMIN' || PLATFORM.has(role);
  return PLATFORM.has(role);
}

/** Groupes et sections visibles pour un rôle ; les groupes vides sont retirés. */
export function helpFor(role: UserRole): HelpGroup[] {
  return HELP_GROUPS.map((g) => ({
    ...g,
    sections: g.sections.filter((s) => canSee(s.audience, role)),
  })).filter((g) => g.sections.length > 0);
}

export const HELP_GROUPS: HelpGroup[] = [
  {
    title: 'Prise en main',
    sections: [
      {
        id: 'connexion',
        title: 'Se connecter et sécuriser son compte',
        audience: 'all',
        blocks: [
          {
            type: 'p',
            text: 'Votre premier accès se fait depuis le lien d’invitation reçu par e-mail : il vous permet de choisir votre mot de passe. Ensuite, connectez-vous avec votre adresse e-mail et ce mot de passe.',
          },
          {
            type: 'image',
            shot: 'connexion',
            alt: 'Page de connexion du dashboard',
            caption:
              'La page de connexion : adresse e-mail, mot de passe et lien « Mot de passe oublié ? ».',
            width: 1280,
            height: 800,
          },
          {
            type: 'list',
            items: [
              {
                term: 'Mot de passe oublié',
                text: 'sur la page de connexion, cliquez sur « Mot de passe oublié ? » et suivez le lien reçu par e-mail. Une fois connecté, « Réinitialiser mon mot de passe » en bas du menu envoie le même e-mail.',
              },
              {
                term: 'Double authentification',
                text: 'les comptes Adweb-Systèmes l’activent à la première connexion. Scannez le QR code avec une application d’authentification (Google Authenticator, Microsoft Authenticator…), puis saisissez le code à 6 chiffres. Ce code est ensuite demandé à chaque connexion.',
              },
              {
                term: 'Déconnexion',
                text: 'bouton « Déconnexion » en bas du menu.',
              },
            ],
          },
        ],
      },
      {
        id: 'affichage',
        title: 'Personnaliser l’affichage',
        audience: 'all',
        blocks: [
          {
            type: 'steps',
            items: [
              'Cliquez sur « Affichage » en bas du menu.',
              'Choisissez un style : Marine (bleu nuit, par défaut), Clarté (tout clair), Continuité (le style historique) ou Sombre (mode sombre).',
              'Choisissez la présentation des automatisations : Liste ou Tuiles, et en tuiles, le nombre par ligne (3, 4 ou 5).',
            ],
          },
          {
            type: 'image',
            shot: 'affichage',
            alt: 'Fenêtre Affichage avec le choix du style et de la présentation',
            caption:
              'La fenêtre « Affichage » : quatre styles, dont le mode sombre, et deux présentations des automatisations.',
            width: 672,
            height: 694,
          },
          {
            type: 'p',
            text: 'Les changements s’appliquent tout de suite et ne concernent que vous. La bascule Liste / Tuiles est aussi disponible en haut de la page Automatisations.',
          },
          {
            type: 'image',
            shot: 'synthese-sombre',
            alt: 'La Synthèse en mode sombre',
            caption: 'Le style Sombre : fonds foncés et textes clairs sur toutes les pages.',
            width: 1280,
            height: 800,
          },
          {
            type: 'p',
            text: 'Sur ordinateur, « Réduire le menu » (en bas de la liste des pages) ne garde que les icônes de la barre latérale, pour laisser plus de place au contenu. Survolez une icône pour voir le nom de la page ; le bouton fléché la rouvre. Votre choix est retenu d’une connexion à l’autre.',
          },
          {
            type: 'image',
            shot: 'menu-reduit',
            alt: 'Barre latérale réduite aux icônes',
            caption: 'Le menu réduit : seules les icônes des pages restent affichées.',
            width: 1280,
            height: 800,
          },
          {
            type: 'tip',
            text: '« Revenir au réglage de mon organisation » annule vos choix personnels et rétablit l’affichage prévu pour votre organisation.',
          },
        ],
      },
    ],
  },
  {
    title: 'Au quotidien',
    sections: [
      {
        id: 'synthese',
        title: 'Lire la Synthèse',
        audience: 'all',
        blocks: [
          {
            type: 'p',
            text: 'La Synthèse est la page d’accueil. Pour les comptes clients, l’« Accès rapide » en haut de page présente vos automatisations favorites, ou à défaut les plus utilisées, pour les lancer sans chercher. La liste complète est dans « Automatisations ». « Masquer » le retire de l’accueil ; pour le remettre, cochez « Accès rapide sur l’accueil » dans « Affichage ».',
          },
          {
            type: 'image',
            shot: 'synthese',
            alt: 'Page Synthèse vue par Adweb-Systèmes',
            caption:
              'Vue Adweb-Systèmes : le filtre d’organisation et la période se règlent en haut à droite (encadrés).',
            width: 1280,
            height: 800,
          },
          {
            type: 'list',
            items: [
              { term: 'Automatisations actives', text: 'nombre d’automatisations en service.' },
              { term: 'Exécutions', text: 'nombre de lancements sur la période choisie.' },
              {
                term: 'Taux de succès',
                text: 'part des exécutions réussies, avec le détail succès / échecs.',
              },
              {
                term: 'Temps gagné',
                text: 'estimation calculée à partir du gain par exécution indiqué pour chaque automatisation.',
              },
            ],
          },
          {
            type: 'p',
            text: 'Choisissez la période en haut de la page : 7 jours, 30 jours, 90 jours ou 12 mois. Les graphiques montrent l’évolution des exécutions (succès et échecs) et du temps gagné, puis le détail par automatisation et les dernières exécutions.',
          },
        ],
      },
      {
        id: 'lancer',
        title: 'Lancer une automatisation',
        audience: 'all',
        blocks: [
          {
            type: 'p',
            text: 'Chaque automatisation a un bouton d’action : dans la liste, sur sa tuile et en haut de sa fiche. Son libellé dit ce qui va se passer.',
          },
          {
            type: 'image',
            shot: 'lancer-liste',
            alt: 'Liste des automatisations avec la colonne Action',
            caption:
              'Dans la liste, la colonne « Action » (encadrée) donne le bon bouton pour chaque automatisation.',
            width: 1280,
            height: 800,
          },
          {
            type: 'list',
            items: [
              {
                term: 'Exécuter',
                text: 'ouvre une fenêtre pour saisir les informations demandées, puis lance le traitement. Le résultat apparaît dans Exécutions.',
              },
              {
                term: 'Remplir le formulaire',
                text: 'ouvre le formulaire dans un nouvel onglet ; le traitement démarre à l’envoi.',
              },
              {
                term: 'Ouvrir le chat',
                text: 'ouvre l’assistant conversationnel dans un nouvel onglet.',
              },
              {
                term: 'Automatique',
                text: 'l’automatisation se lance seule selon sa planification (par exemple « lundi 8 h ») ; vous n’avez rien à faire.',
              },
              {
                term: 'À réception d’un e-mail',
                text: 'l’automatisation se lance quand un e-mail arrive dans la boîte surveillée.',
              },
            ],
          },
          {
            type: 'image',
            shot: 'accueil-client',
            alt: 'Accueil d’un compte client avec l’accès rapide en tuiles',
            caption:
              'Sur l’accueil d’un compte client, l’accès rapide présente les favoris ou les automatisations les plus utilisées, ici en tuiles.',
            width: 1280,
            height: 800,
          },
          {
            type: 'p',
            text: 'Pour retrouver une automatisation, tapez quelques lettres dans « Rechercher… ». Cliquez sur l’étoile pour l’ajouter à vos favoris : elle passe en tête de liste dans Automatisations, le bouton « Favoris » n’affiche qu’elles, et l’accès rapide de l’accueil ne montre plus que vos favoris.',
          },
          {
            type: 'tip',
            text: '« Indisponible » ou « En préparation » signifie que l’automatisation est en pause ou pas encore en service. Si une automatisation n’apparaît pas du tout, demandez l’accès à l’administrateur de votre organisation.',
          },
          {
            type: 'image',
            shot: 'lancer-modale',
            alt: 'Fenêtre d’exécution avec les informations à saisir',
            caption:
              '« Exécuter » ouvre cette fenêtre : remplissez les champs marqués d’un astérisque, puis « Exécuter maintenant ».',
            width: 512,
            height: 458,
          },
        ],
      },
      {
        id: 'executions',
        title: 'Suivre ses exécutions',
        audience: 'all',
        blocks: [
          {
            type: 'p',
            text: 'La page Exécutions liste chaque lancement : automatisation, statut, déclencheur, heure de début et durée. Elle se met à jour toute seule toutes les 5 secondes.',
          },
          {
            type: 'image',
            shot: 'executions',
            alt: 'Journal des exécutions avec filtres et bouton Relancer',
            caption:
              'Filtrez par statut en haut ; « Relancer » apparaît sur un échec pour les administrateurs.',
            width: 1280,
            height: 800,
          },
          {
            type: 'list',
            items: [
              { term: 'En cours', text: 'le traitement tourne encore.' },
              { term: 'Succès', text: 'le traitement s’est terminé normalement.' },
              { term: 'Échec', text: 'le traitement s’est arrêté sur une erreur.' },
              { term: 'Partiel', text: 'une partie seulement a pu être traitée.' },
            ],
          },
          {
            type: 'image',
            shot: 'execution-echec',
            alt: 'Détail d’une exécution en échec',
            caption: 'Le détail d’un échec indique le message d’erreur et l’étape qui a échoué.',
            width: 1280,
            height: 800,
          },
          {
            type: 'p',
            text: 'Filtrez par statut en haut de la liste. « Détail » ouvre une exécution : résumé du résultat, ou message d’erreur et étape en échec.',
          },
        ],
      },
      {
        id: 'executions-admin',
        title: 'Relancer un échec et exporter le journal',
        audience: 'clientAdmin',
        blocks: [
          {
            type: 'list',
            items: [
              {
                term: 'Relancer',
                text: 'sur une exécution en échec d’une automatisation de type « Exécuter », le bouton « Relancer » la relance avec les mêmes informations.',
              },
              {
                term: 'Exporter',
                text: '« Exporter PDF » et « Exporter CSV » téléchargent le journal avec les filtres en cours.',
              },
            ],
          },
        ],
      },
      {
        id: 'rapports',
        title: 'Générer un rapport',
        audience: 'clientAdmin',
        blocks: [
          {
            type: 'steps',
            items: [
              'Sur la Synthèse, descendez jusqu’à la section « Rapports ».',
              'Choisissez la période (hebdomadaire ou mensuelle) et le format (PDF ou CSV).',
              'Cliquez sur « Générer ». Le rapport apparaît « En préparation… », puis le bouton « Télécharger » s’affiche tout seul quand il est prêt.',
            ],
          },
          {
            type: 'image',
            shot: 'rapports',
            alt: 'Section Rapports de la Synthèse',
            caption:
              'Choisissez la période et le format, puis « Générer ». « Télécharger » apparaît quand le rapport est prêt.',
            width: 1040,
            height: 200,
          },
        ],
      },
      {
        id: 'demander',
        title: 'Demander une automatisation',
        audience: 'all',
        blocks: [
          {
            type: 'steps',
            items: [
              'Cliquez sur « Demander une automatisation » (page Automatisations, accueil ou menu « Demandes »).',
              'Donnez un titre, décrivez la tâche que vous faites aujourd’hui à la main, sa fréquence et les outils concernés.',
              'Envoyez : Adweb-Systèmes reçoit votre demande immédiatement.',
            ],
          },
          {
            type: 'image',
            shot: 'demande-formulaire',
            alt: 'Formulaire Demander une automatisation',
            caption:
              'Le formulaire : un titre, votre besoin, la fréquence et les outils concernés.',
            width: 512,
            height: 512,
          },
          {
            type: 'p',
            text: 'Le menu « Demandes » liste vos demandes et leur avancement : Nouvelle, À l’étude, Planifiée, Réalisée ou Non retenue. À chaque changement, vous êtes prévenu par e-mail et dans la cloche.',
          },
          {
            type: 'image',
            shot: 'demandes',
            alt: 'Liste des demandes avec leur statut',
            caption:
              'Le menu « Demandes » : chaque demande avec son statut, le dernier message et le badge « Nouveau message ».',
            width: 1280,
            height: 800,
          },
          {
            type: 'p',
            text: 'Ouvrez une demande pour échanger avec Adweb-Systèmes : posez une question, apportez une précision, transmettez une information. Chaque réponse d’Adweb-Systèmes vous est signalée par e-mail, dans la cloche et par le badge « Nouveau message ».',
          },
          {
            type: 'image',
            shot: 'demande-echanges',
            alt: 'Fiche d’une demande avec le fil d’échanges',
            caption:
              'Les échanges d’une demande : vos messages à droite, ceux d’Adweb-Systèmes à gauche, les changements de statut au centre.',
            width: 672,
            height: 770,
          },
        ],
      },
      {
        id: 'suggestions',
        title: 'Suggestions d’Adweb-Systèmes',
        audience: 'all',
        blocks: [
          {
            type: 'p',
            text: 'Le menu « Suggestions » présente des services testés par Adweb-Systèmes et prêts à vous faire gagner du temps : assistant qui répond aux questions sur vos documents, relances, comptes rendus… Une pastille signale les nouveautés depuis votre dernière visite.',
          },
          {
            type: 'image',
            shot: 'suggestions',
            alt: 'Vitrine des suggestions avec le badge Nouveau',
            caption:
              'La vitrine : chaque service avec son gain de temps estimé ; « Demande envoyée » quand votre organisation l’a déjà demandé.',
            width: 1280,
            height: 800,
          },
          {
            type: 'steps',
            items: [
              'Cliquez sur une suggestion pour lire sa présentation et un exemple concret.',
              '« Ça m’intéresse » : ajoutez une précision si vous le souhaitez. Une demande est créée et Adweb-Systèmes est prévenu, sans engagement.',
              'Suivez la suite et échangez avec Adweb-Systèmes dans le menu « Demandes ».',
            ],
          },
          {
            type: 'image',
            shot: 'suggestion-fiche',
            alt: 'Fiche d’une suggestion avec les boutons Ça m’intéresse et Pas pour moi',
            caption:
              'La fiche d’une suggestion : « Ça m’intéresse » crée une demande, « Pas pour moi » la masque.',
            width: 672,
            height: 664,
          },
          {
            type: 'tip',
            text: '« Pas pour moi » masque la suggestion pour toute votre organisation. Elle reste accessible en bas de page, dans « Suggestions masquées », d’où vous pouvez la réafficher.',
          },
        ],
      },
      {
        id: 'notifications',
        title: 'Notifications',
        audience: 'all',
        blocks: [
          {
            type: 'p',
            text: 'La cloche en haut de l’écran signale les nouveaux événements qui vous concernent, comme l’échec d’une automatisation. Le chiffre indique les notifications non lues. Ouvrez une notification pour la marquer comme lue, ou utilisez « Tout marquer comme lu ».',
          },
          {
            type: 'image',
            shot: 'notifications',
            alt: 'Liste des notifications ouverte depuis la cloche',
            caption:
              'La cloche ouvre la liste des notifications ; les non lues sont surlignées en bleu.',
            width: 1280,
            height: 800,
          },
          {
            type: 'tip',
            text: 'Le bouton « Nouveautés », à côté de la cloche, présente les dernières évolutions du dashboard. Une pastille orange signale qu’il y a du nouveau depuis votre dernière visite.',
          },
        ],
      },
      {
        id: 'donnees',
        title: 'Mes données et confidentialité',
        audience: 'all',
        blocks: [
          {
            type: 'p',
            text: '« Mes données », en bas du menu, présente votre compte, l’état de la double authentification et vos droits sur vos données personnelles (accès, rectification, effacement, portabilité…). Les liens en bas du menu ouvrent les mentions légales, les CGU, les CGV et la politique de confidentialité.',
          },
          {
            type: 'tip',
            text: 'Les administrateurs peuvent y télécharger un export complet des données de l’organisation (fichier JSON, sans les secrets techniques).',
          },
        ],
      },
    ],
  },
  {
    title: 'Administrer votre organisation',
    sections: [
      {
        id: 'utilisateurs',
        title: 'Gérer les utilisateurs et leurs accès',
        audience: 'clientAdmin',
        blocks: [
          {
            type: 'steps',
            items: [
              'Ouvrez « Utilisateurs », puis « Inviter un utilisateur ».',
              'Saisissez son nom, son e-mail et son rôle.',
              'La personne reçoit un e-mail pour créer son mot de passe.',
            ],
          },
          {
            type: 'image',
            shot: 'utilisateurs',
            alt: 'Page Utilisateurs avec les boutons Inviter et Accès',
            caption:
              '« Inviter un utilisateur » en haut à droite, « Accès » sur chaque utilisateur client.',
            width: 1280,
            height: 800,
          },
          {
            type: 'list',
            items: [
              {
                term: 'Administrateur client',
                text: 'voit toute l’organisation, gère les utilisateurs, relance les échecs, exporte le journal et génère les rapports.',
              },
              {
                term: 'Utilisateur client',
                text: 'ne voit que les automatisations auxquelles vous lui donnez accès.',
              },
            ],
          },
          {
            type: 'p',
            text: 'Pour un utilisateur client, le bouton « Accès » liste les automatisations de l’organisation : cochez « Peut exécuter » pour chacune qu’il doit voir et lancer. Une automatisation non cochée lui reste invisible.',
          },
          {
            type: 'tip',
            text: '« Désactiver » bloque temporairement la connexion d’un compte et ferme ses sessions ouvertes ; « Réactiver » la rétablit. Vous pouvez aussi mettre en pause une automatisation de votre organisation depuis la liste (« Désactiver »).',
          },
          {
            type: 'image',
            shot: 'acces',
            alt: 'Fenêtre d’accès aux automatisations d’un utilisateur',
            caption: 'Cochez les automatisations que la personne peut voir et lancer.',
            width: 512,
            height: 360,
          },
        ],
      },
    ],
  },
  {
    title: 'Espace Adweb-Systèmes',
    sections: [
      {
        id: 'organisations',
        title: 'Créer et gérer une organisation',
        audience: 'platform',
        blocks: [
          {
            type: 'steps',
            items: [
              'Administration › Organisations › « Créer une organisation » (super-administrateur).',
              'Invitez ensuite son administrateur depuis Utilisateurs en choisissant cette organisation comme organisation cible.',
              'Référencez ses automatisations (voir ci-dessous), puis donnez les accès.',
            ],
          },
          {
            type: 'image',
            shot: 'organisations',
            alt: 'Liste des organisations avec l’action Affichage',
            caption: '« Affichage » (encadré) règle le style par défaut de chaque organisation.',
            width: 1280,
            height: 800,
          },
          {
            type: 'list',
            items: [
              {
                term: 'Affichage',
                text: 'fixe le style et la présentation par défaut pour tous les utilisateurs de l’organisation qui n’ont pas fait leur propre choix.',
              },
              {
                term: 'Suspendre / Réactiver',
                text: 'coupe ou rétablit l’accès de toute l’organisation.',
              },
              { term: 'Exporter', text: 'télécharge toutes ses données (portabilité RGPD).' },
              {
                term: 'Supprimer',
                text: 'efface définitivement l’organisation et ses données (droit à l’oubli). Irréversible.',
              },
            ],
          },
        ],
      },
      {
        id: 'referencer',
        title: 'Référencer une automatisation n8n',
        audience: 'platform',
        blocks: [
          {
            type: 'steps',
            items: [
              'Automatisations › « Référencer une automatisation ».',
              'Choisissez l’organisation, puis renseignez le nom, l’identifiant (en minuscules avec tirets), la catégorie, le type et le statut.',
              'Selon le type : adresse du formulaire ou du chat, libellé de planification, boîte e-mail surveillée, ou adresse et secret du webhook n8n.',
              'Ajoutez les paramètres à demander lors d’une exécution et le gain estimé par exécution (en minutes), qui alimente le temps gagné.',
            ],
          },
          {
            type: 'image',
            shot: 'referencer',
            alt: 'Fenêtre Référencer une automatisation',
            caption: 'Les champs affichés s’adaptent au type de déclenchement choisi.',
            width: 512,
            height: 764,
          },
          {
            type: 'tip',
            text: 'L’adresse et le secret du webhook sont chiffrés et ne sont jamais réaffichés. Pour les changer, saisissez-en de nouveaux dans « Modifier » : bouton en haut de la fiche de l’automatisation, ou menu « ⋯ » dans la liste et sur les tuiles.',
          },
        ],
      },
      {
        id: 'n8n',
        title: 'Relier un workflow n8n au dashboard',
        audience: 'platform',
        blocks: [
          {
            type: 'p',
            text: 'Le dashboard ne fait pas tourner les traitements : n8n les exécute et lui rend compte de chaque exécution.',
          },
          {
            type: 'list',
            items: [
              'Chaque workflow envoie « exécution démarrée » puis « exécution réussie », signés avec le secret d’ingestion, en reprenant l’identifiant de l’exécution n8n.',
              'Les échecs passent par le workflow d’erreur global : ajoutez-y l’identifiant de chaque nouvelle automatisation. Un échec ouvre un incident et prévient les destinataires concernés.',
              'Les identifiants de l’organisation et de l’automatisation sont copiables depuis la fiche de l’automatisation.',
            ],
          },
          {
            type: 'tip',
            text: 'Le contrat technique complet (format des événements, signature, idempotence) est décrit dans docs/04-INTEGRATION-N8N.md du dépôt.',
          },
        ],
      },
      {
        id: 'demandes-adweb',
        title: 'Traiter les demandes des clients',
        audience: 'platform',
        blocks: [
          {
            type: 'p',
            text: 'Chaque nouvelle demande arrive par e-mail et dans la cloche de tous les comptes Adweb. Le menu « Demandes » les regroupe, avec un filtre par organisation et par statut.',
          },
          {
            type: 'steps',
            items: [
              'Ouvrez la demande depuis la liste, l’e-mail ou la notification (badge « Nouveau message » quand le client a écrit).',
              'Choisissez le nouveau statut puis « Changer le statut » : le demandeur est prévenu et le changement s’inscrit dans les échanges.',
              'Écrivez au client dans les échanges : il est prévenu par e-mail et dans sa cloche, et peut vous répondre.',
            ],
          },
          {
            type: 'image',
            shot: 'demande-traitement',
            alt: 'Fiche d’une demande avec le statut et les échanges avec le client',
            caption:
              'La fiche d’une demande : le statut en haut, puis les échanges avec le client.',
            width: 672,
            height: 886,
          },
        ],
      },
      {
        id: 'suggestions-adweb',
        title: 'Proposer des services (Suggestions)',
        audience: 'platform',
        blocks: [
          {
            type: 'p',
            text: 'Le menu « Suggestions » est votre vitrine : proposez aux clients les services que vous avez construits et testés, puis mesurez leur intérêt avant de les déployer.',
          },
          {
            type: 'steps',
            items: [
              '« Nouvelle suggestion » : titre, accroche, description, exemple concret, catégorie, gain estimé, icône et couleur.',
              'Choisissez à qui la proposer : toutes les organisations clientes, ou certaines seulement.',
              'La suggestion naît en brouillon, visible des seuls comptes Adweb : le temps de tester le service.',
              '« Publier » la rend visible des clients concernés ; cochez « Prévenir les clients » pour leur envoyer un e-mail et une notification.',
            ],
          },
          {
            type: 'image',
            shot: 'suggestions-adweb',
            alt: 'Liste des suggestions côté Adweb avec statut et nombre d’intéressés',
            caption:
              'Vos suggestions : statut, organisations visées et nombre de clients intéressés.',
            width: 1280,
            height: 800,
          },
          {
            type: 'p',
            text: 'Quand un client clique « Ça m’intéresse », une demande marquée « Suggestion » arrive dans le menu « Demandes », avec e-mail et notification comme toute nouvelle demande. La fiche de la suggestion liste les organisations intéressées et le statut de leur demande.',
          },
          {
            type: 'image',
            shot: 'suggestion-diffusion',
            alt: 'Fiche d’une suggestion côté Adweb : diffusion et intérêt des clients',
            caption:
              'La fiche : aperçu de ce que voient les clients, diffusion, puis organisations intéressées.',
            width: 672,
            height: 714,
          },
          {
            type: 'tip',
            text: 'Une suggestion à laquelle des clients ont répondu ne se supprime pas : archivez-la. Elle quitte la vitrine, les demandes reçues restent.',
          },
        ],
      },
      {
        id: 'cout-ia',
        title: 'Suivre le coût IA',
        audience: 'platform',
        blocks: [
          {
            type: 'p',
            text: 'Le coût IA et les jetons consommés, remontés par n8n à chaque exécution, sont visibles du seul super-administrateur : carte « Coût IA » et colonne dans les tableaux de la Synthèse, colonne dans le journal des exécutions, détail dans chaque exécution, et colonnes de l’export CSV.',
          },
          {
            type: 'tip',
            text: 'Ni les clients ni les opérateurs Adweb ne les voient, y compris dans les rapports envoyés aux clients. Pour qu’un coût remonte, le workflow n8n doit l’envoyer avec l’exécution (voir docs/04-INTEGRATION-N8N.md).',
          },
        ],
      },
      {
        id: 'incidents',
        title: 'Traiter un incident',
        audience: 'platform',
        blocks: [
          {
            type: 'p',
            text: 'Un incident est créé automatiquement à chaque échec d’exécution. Faites-le passer de « Ouvert » à « En cours », puis à « Résolu ».',
          },
          {
            type: 'image',
            shot: 'incidents',
            alt: 'Liste des incidents',
            caption: 'Chaque échec crée un incident ; son statut passe de « Ouvert » à « Résolu ».',
            width: 1280,
            height: 800,
          },
          {
            type: 'tip',
            text: 'La note interne d’un incident n’est jamais visible des comptes clients : utilisez-la pour le diagnostic.',
          },
        ],
      },
      {
        id: 'audit',
        title: 'Consulter le journal d’audit',
        audience: 'platform',
        blocks: [
          {
            type: 'p',
            text: 'Le journal d’audit trace les actions sensibles : connexions, déclenchements, changements de droits et de configuration. Utilisez-le pour savoir qui a fait quoi et quand.',
          },
        ],
      },
      {
        id: 'mise-a-jour',
        title: 'Publier une mise à jour du dashboard',
        audience: 'platform',
        blocks: [
          {
            type: 'p',
            text: 'Chaque modification passe par une branche et une pull request. Une fois la vérification automatique réussie et la pull request fusionnée, le dashboard est redéployé automatiquement en production.',
          },
          {
            type: 'tip',
            text: 'La procédure détaillée (sauvegarde, migrations, relance manuelle, retour arrière) est décrite dans docs/07-DEPLOIEMENT.md du dépôt.',
          },
        ],
      },
    ],
  },
];
